import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

type PdfOrientation = 'p' | 'portrait' | 'l' | 'landscape';
type PdfImageType = 'JPEG' | 'PNG';

export interface ExportElementToPdfOptions {
	element: HTMLElement;
	filename: string;
	orientation?: PdfOrientation;
	margin?: number;
	imageType?: PdfImageType;
	backgroundColor?: string;
	blockSelector?: string;
	blockGap?: number;
}

/**
 * Rasterize a DOM element into a canvas using the same visual styling currently
 * shown in the browser. This is the only place where HTML is converted into
 * pixels; the rest of the file works with canvas slices and PDF image placement.
 */
const captureElement = async (element: HTMLElement, backgroundColor: string) => {
	return html2canvas(element, {
		useCORS: true,
		allowTaint: true,
		scrollY: -window.scrollY,
		scale: 2,
		backgroundColor,
	});
};

/**
 * Copy a vertical section of a larger canvas into a new canvas.
 *
 * jsPDF receives page images one at a time, so tall screenshots must be split
 * into page-sized pieces before they are inserted into the PDF.
 */
const createCanvasSlice = (canvas: HTMLCanvasElement, y: number, height: number, backgroundColor: string) => {
	const pageCanvas = document.createElement('canvas');
	const pageContext = pageCanvas.getContext('2d');

	pageCanvas.width = canvas.width;
	pageCanvas.height = Math.ceil(height);

	if (!pageContext) return null;

	pageContext.fillStyle = backgroundColor;
	pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
	pageContext.drawImage(canvas, 0, y, canvas.width, height, 0, 0, canvas.width, height);

	return pageCanvas;
};

/**
 * Return visible child elements that are worth considering as independent
 * pagination units. Hidden or zero-sized elements are ignored because they
 * would not contribute anything useful to the rendered PDF.
 */
const getPrintableChildren = (element: HTMLElement) => {
	return Array.from(element.children).filter((child): child is HTMLElement => {
		if (!(child instanceof HTMLElement)) return false;
		const rect = child.getBoundingClientRect();
		return rect.width > 0 && rect.height > 0;
	});
};

/**
 * Estimate how tall an element will be once scaled to the target PDF image width.
 *
 * Browser layout is measured in CSS pixels, while jsPDF placement uses PDF units.
 * This ratio preserves the element's aspect ratio across those coordinate spaces.
 */
const estimatePdfHeight = (element: HTMLElement, imageWidth: number) => {
	const rect = element.getBoundingClientRect();
	if (rect.width <= 0) return 0;
	return (rect.height * imageWidth) / rect.width;
};

/**
 * Heuristic for small heading-like elements that should stay with the following
 * content block instead of being stranded at the bottom of a page.
 */
const isKeepWithNextCandidate = (element: HTMLElement, pageContentHeight: number, imageWidth: number) => {
	const tagName = element.tagName.toUpperCase();
	const estimatedHeight = estimatePdfHeight(element, imageWidth);
	const text = element.textContent?.trim() ?? '';

	if (!text || estimatedHeight <= 0 || estimatedHeight > pageContentHeight * 0.2) return false;
	if (/^H[1-6]$/.test(tagName)) return true;

	const childElementCount = element.children.length;
	const styles = window.getComputedStyle(element);
	const fontWeight = Number(styles.fontWeight) || 400;
	const fontSize = Number.parseFloat(styles.fontSize) || 0;

	return childElementCount === 0 && ['DIV', 'P'].includes(tagName) && (fontWeight >= 600 || fontSize >= 16);
};

/**
 * Capture a DOM element and export it as a multi-page PDF.
 *
 * This centralizes the report-printing pattern used by BlockScorecard and other
 * report pages: render once with html2canvas, then slice the resulting canvas
 * across PDF pages so tall content is paginated instead of squeezed or clipped.
 *
 * There are two rendering modes:
 * - Without `blockSelector`, the entire element is captured once and split into
 *   page-height slices. This is simple and reliable, but page breaks may cut
 *   through visual sections.
 * - With `blockSelector`, matching blocks are captured and placed one by one.
 *   Oversized blocks are recursively broken into children before falling back to
 *   pixel slicing. This keeps headings, cards, tables, and sections together
 *   whenever possible.
 */
export async function exportElementToPdf({
	element,
	filename,
	orientation = 'p',
	margin = 10,
	imageType = 'JPEG',
	backgroundColor = '#ffffff',
	blockSelector,
	blockGap = 6,
}: ExportElementToPdfOptions): Promise<void> {
	const doc = new jsPDF({ orientation });

	// Ensure web fonts are ready before rasterizing text-heavy HTML content.
	await document.fonts?.ready;

	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const imageWidth = pageWidth - margin * 2;
	const pageContentHeight = pageHeight - margin * 2;

	if (blockSelector) {
		// Block-aware mode favors semantic page breaks. Instead of slicing the full
		// report immediately, it tries to render each selected block as a unit.
		const blocks = Array.from(element.querySelectorAll<HTMLElement>(blockSelector));
		let cursorY = margin;

		const addCanvasToPdf = (canvas: HTMLCanvasElement, y: number, height: number) => {
			// Convert the canvas to an image at insertion time. Width is fixed to the
			// printable PDF width, and callers provide the matching scaled height.
			const imageData = canvas.toDataURL(`image/${imageType.toLowerCase()}`);
			doc.addImage(imageData, imageType, margin, y, imageWidth, height);
		};

		const renderCanvasSlices = (canvas: HTMLCanvasElement, startY: number) => {
			// Last-resort pagination for content that cannot be split cleanly into
			// child elements. It starts in any remaining page space, then continues on
			// new pages until the entire canvas has been consumed.
			let nextCursorY = startY;
			let renderedHeight = 0;

			while (renderedHeight < canvas.height) {
				const availablePdfHeight = pageHeight - margin - nextCursorY;

				if (availablePdfHeight <= 0) {
					doc.addPage();
					nextCursorY = margin;
					continue;
				}

				// Convert available PDF height back into source-canvas pixels so the slice
				// maps to exactly the space left on the current PDF page.
				const availableCanvasHeight = (availablePdfHeight * canvas.width) / imageWidth;
				const sliceHeight = Math.min(availableCanvasHeight, canvas.height - renderedHeight);
				const pageCanvas = createCanvasSlice(canvas, renderedHeight, sliceHeight, backgroundColor);

				if (!pageCanvas) break;

				const sliceImageHeight = (sliceHeight * imageWidth) / canvas.width;
				addCanvasToPdf(pageCanvas, nextCursorY, sliceImageHeight);
				renderedHeight += sliceHeight;

				if (renderedHeight < canvas.height) {
					doc.addPage();
					nextCursorY = margin;
				} else {
					nextCursorY += sliceImageHeight + blockGap;
				}
			}

			return nextCursorY;
		};

		const renderBlock = async (block: HTMLElement): Promise<void> => {
			// Capture the candidate block first. If it fits, we can place it as one
			// image and avoid cutting through its internal layout.
			const canvas = await captureElement(block, backgroundColor);
			const imageHeight = (canvas.height * imageWidth) / canvas.width;

			if (imageHeight <= pageContentHeight) {
				// Move a whole block to the next page when it would otherwise overflow.
				if (cursorY > margin && cursorY + imageHeight > pageHeight - margin) {
					doc.addPage();
					cursorY = margin;
				}

				addCanvasToPdf(canvas, cursorY, imageHeight);
				cursorY += imageHeight + blockGap;
				return;
			}

			const children = getPrintableChildren(block);

			if (children.length > 1) {
				for (let index = 0; index < children.length; index += 1) {
					const child = children[index];
					const nextChild = children[index + 1];

					// Keep short heading-like content with the block that follows it. This
					// prevents a heading from appearing alone at the bottom of a page.
					if (nextChild && isKeepWithNextCandidate(child, pageContentHeight, imageWidth) && cursorY > margin) {
						const childHeight = estimatePdfHeight(child, imageWidth);
						const nextChildHeight = estimatePdfHeight(nextChild, imageWidth);
						const combinedHeight = childHeight + blockGap + nextChildHeight;

						if (combinedHeight <= pageContentHeight && cursorY + combinedHeight > pageHeight - margin) {
							doc.addPage();
							cursorY = margin;
						}
					}

					await renderBlock(child);
				}
				return;
			}

			if (children.length === 1) {
				const child = children[0];
				const childRect = child.getBoundingClientRect();
				const blockRect = block.getBoundingClientRect();

				// If the wrapper contributes little of its own layout, recurse into the child
				// so markdown sections/lists/tables can move as units before we slice pixels.
				if (childRect.height >= blockRect.height * 0.75) {
					await renderBlock(child);
					return;
				}
			}

			// Final fallback for genuinely oversized atomic content such as a large table,
			// image, or chart: preserve content by slicing, starting in remaining space when available.
			cursorY = renderCanvasSlices(canvas, cursorY);
		};

		for (const block of blocks) {
			await renderBlock(block);
		}

		doc.save(filename);
		return;
	}

	// Default mode: capture the entire element once, then slice the resulting
	// canvas into page-height chunks. This preserves all content but does not try
	// to align page breaks with semantic sections.
	const canvas = await captureElement(element, backgroundColor);

	const imageHeight = (canvas.height * imageWidth) / canvas.width;
	const pageCanvasHeight = (pageContentHeight * canvas.width) / imageWidth;
	let renderedHeight = 0;
	let pageIndex = 0;

	while (renderedHeight < canvas.height) {
		// `sliceHeight` is measured in canvas pixels. `sliceImageHeight` below is
		// the matching height in PDF units after scaling to `imageWidth`.
		const sliceHeight = Math.min(pageCanvasHeight, canvas.height - renderedHeight);
		const pageCanvas = createCanvasSlice(canvas, renderedHeight, sliceHeight, backgroundColor);

		if (!pageCanvas) break;

		const imageData = pageCanvas.toDataURL(`image/${imageType.toLowerCase()}`);
		const sliceImageHeight = Math.min(imageHeight - pageIndex * pageContentHeight, pageContentHeight);

		if (pageIndex > 0) {
			doc.addPage();
		}

		doc.addImage(imageData, imageType, margin, margin, imageWidth, sliceImageHeight);

		renderedHeight += sliceHeight;
		pageIndex += 1;
	}

	doc.save(filename);
}
